import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import GridContainer from "@/components/common/GridContainer"
import { getCssSizeValue, getMapKey } from "@/utils/utils"
import styled from "styled-components"
import { BREAKPOINTS } from "@/styles/themeSettings"
import CommonPaginationBar from "@/components/common/PaginationBar"
import useScrollContext from "@/hooks/useScrollContext"
import usePressActions from "@/components/Press/usePressActions"
import PressAction, { Skeleton as PressActionSkeleton } from "@/components/Press/Action"
import { useIsMobile } from "@/hooks/useDevice"
import useEmblaCarousel from "embla-carousel-react"

const Wrapper = styled.div`
	display: flex;
	overflow: hidden;
	flex-direction: column;
	row-gap: ${getCssSizeValue(40, BREAKPOINTS.MOBILE)};

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		row-gap: ${getCssSizeValue(30, BREAKPOINTS.DESKTOP)};
	}
`

const Container = styled(GridContainer)`
	width: 100%;

	.press-action {
		//animation: fade-in var(--rgr-duration) var(--ease);
		animation: fade-in calc(var(--rgr-duration) + var(--delay)) ease-out;
	}

	@media (max-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.TABLET] - 1}px) {
		//margin-top: ${getCssSizeValue(40, BREAKPOINTS.MOBILE)};
		display: flex !important;
		.press-action {
			flex: 0 0 100%;
			min-width: 0;
		}
	}
`

const Pagination = styled(CommonPaginationBar)`
	margin-left: auto;
	margin-right: auto;
`

const ACTIONS_PER_PAGE = 4

export function createNestedArray(size, itemsPerPage = 4) {
	let result = new Array(size)
	for (let i = 0; i < size; i++) {
		result[i] = new Array(itemsPerPage).fill(null)
	}
	return result
}

function useMediaPagination(initialActions = [], currentPage = 1, totalPages = 1, itemsPerPage = 4) {
	//format initialActions to proper structure and count num of pages they will take
	const [formattedInitials, initialPagesNum] = useMemo(() => {
		const pagesNum = Math.ceil(initialActions.length / itemsPerPage)
		const initials = [...new Array(pagesNum)].map((_, index) => {
			const firstIndex = index * ACTIONS_PER_PAGE
			const lastIndex = firstIndex + ACTIONS_PER_PAGE
			return initialActions.slice(firstIndex, lastIndex)
		})
		return [initials, pagesNum]
	}, [initialActions, itemsPerPage])

	const [allActions, setAllActions] = useState(createNestedArray(totalPages, itemsPerPage)) //container for all actions
	const fetchedActions = usePressActions(currentPage, itemsPerPage, currentPage > initialPagesNum, initialPagesNum, totalPages)

	//index of current nested array
	const index = useMemo(() => Math.max(1, currentPage) - 1, [currentPage])

	//current actions
	const paginatedActions = useMemo(() => allActions[index], [allActions, index])

	//update all actions
	useEffect(() => {
		// console.log(formattedInitials, fetchedActions.actions);
		setAllActions([...formattedInitials, ...fetchedActions.actions])
	}, [formattedInitials, fetchedActions.actions])

	return [paginatedActions, allActions, index]
}

const MediaFeed = ({ data, count = 0, className = "" }) => {
	const [currentPage, setCurrentPage] = useState(1)
	const ContainerRef = useRef()
	const initialActions = useMemo(() => (Array.isArray(data) ? data : []), [data])
	const totalPages = useMemo(() => Math.ceil(count / ACTIONS_PER_PAGE), [count])
	const isMobile = useIsMobile()
	const [WrapperRef, emblaApi] = useEmblaCarousel({
		loop: true,
		align: "start",
		active: isMobile, //slider only on mobile
	})
	const scroll = useScrollContext()

	const onPaginationUpdate = useCallback(
		(pageNum) => {
			if (emblaApi) {
				emblaApi.scrollTo(0)
			}
			setCurrentPage(pageNum)
			//scroll to module's top
			const { scroll: currScroll } = scroll.props()
			const target = ContainerRef.current.getBoundingClientRect().top + currScroll
			if (target > currScroll) return //skip scrolling if had to scroll down
			scroll.scrollTo({
				top: target,
				offset: -80,
			})
		},
		[scroll, emblaApi],
	)

	const [actions] = useMediaPagination(initialActions, currentPage, totalPages, ACTIONS_PER_PAGE)
	// console.log(actions);
	return (
		<Wrapper ref={WrapperRef} className="col-mobile-8 col-desktop-16">
			<Container ref={ContainerRef} className={`${className}`}>
				{actions.map((item, index, arr) => {
					//returns fallback if loading
					return !item ? (
						<PressActionSkeleton
							key={getMapKey(item, index)}
							className={"col-mobile-8 col-desktop-4"}
							index={index / arr.length}
						/>
					) : (
						<PressAction
							data={item}
							key={getMapKey(item, index)}
							className={"col-mobile-8 col-desktop-4"}
							index={index / arr.length}
						/>
					)
				})}
			</Container>
			<Pagination currentPage={currentPage} numberOfPages={totalPages} handleClick={onPaginationUpdate} />
		</Wrapper>
	)
}

export default MediaFeed
